import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import Search from '../../shared/components/ui/Search';
import Table from '../../shared/components/layout/Table';
import filter from '../../../modules/shared/assets/svg/filter.svg';
import { transformData } from '../../super-admin/utils/transformData';
import { useMutation, useQuery } from '@tanstack/react-query';
import FormInput from '../../shared/components/ui/FormInput';
import {
  getAllBlocks,
  getCloseDeals,
  downloadNoDue,
  downloadLedger
} from '../services/api/implementation/impl';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import { useNavigate, useParams } from 'react-router-dom';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import PaginationComponent from '../../shared/components/PaginationComponent';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import Header from '../../shared/components/layout/Header';
import Layout from '../components/layout/AppLayout';
import Loader from '../../shared/components/ui/Loader';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useForm } from 'react-hook-form';
import { ChevronDown, Download, FileText, BookOpen } from 'lucide-react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';

const InvoicesList = () => {
  const userId = Cookies.get('userId');
  const navigate = useNavigate();

  // Get projects list from context
  const { projects, projectsForDropdown, isProjectsLoading, projectsError } = useProjectsList();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    setValue,
    watch
  } = useForm();
  const { collapsed } = useSidebar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [blockID, setBlockID] = useState(null);
  const [transformedDataBlocks, setTransformedDataBlocks] = useState([]);
  const blockHeaderRef = useRef(null);
  const [blockDropdownPosition, setBlockDropdownPosition] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    // Load saved project ID from localStorage
    const savedProjectId = localStorage.getItem('invoicesListSelectedProjectId');
    return savedProjectId ? parseInt(savedProjectId) : null;
  });

  // Watch project field changes
  const projectFieldValue = watch('project');

  // pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // items per page
  const [sortDirection, setSortDirection] = useState('');
  const [dueAmountSort, setDueAmountSort] = useState('');
  const {
    data: Invoices,
    refetch: refetchInvoices,
    isFetching: isFetchingInvoices
  } = useQuery({
    queryKey: [
      'deals',
      page,
      selectedProjectId,
      searchQuery,
      blockID,
      sortDirection,
      dueAmountSort
    ],
    queryFn: () =>
      getCloseDeals(
        selectedProjectId,
        page,
        limit,
        searchQuery,
        blockID,
        sortDirection,
        dueAmountSort
      ),
    keepPreviousData: true,
    enabled: !!selectedProjectId
  });

  useEffect(() => {
    if (selectedProjectId) {
      refetchInvoices();
    }
  }, [selectedProjectId, refetchInvoices]);

  const {
    data: blocksDyna,
    refetch: refetchBlocks,
    isFetching: isFetchingBlocks
  } = useQuery({
    queryKey: ['blocks', selectedProjectId],
    queryFn: () => {
      return getAllBlocks(selectedProjectId);
    },
    enabled: !!selectedProjectId
  });

  // Function to save selected project to localStorage
  const saveSelectedProject = (projectId) => {
    localStorage.setItem('invoicesListSelectedProjectId', projectId?.toString());
  };

  useEffect(() => {
    if (projects && projects.length > 0) {
      // If no project is selected, default to first project
      if (!selectedProjectId) {
        setValue('project', projects[0].id);
        setSelectedProjectId(projects[0].id);
        saveSelectedProject(projects[0].id);
      } else {
        // Set the saved project
        setValue('project', selectedProjectId);
      }
    }
  }, [projects, setValue, selectedProjectId]);

  // Handle project field changes
  useEffect(() => {
    if (projectFieldValue && projectFieldValue !== selectedProjectId) {
      const projectId = parseInt(projectFieldValue);
      setSelectedProjectId(projectId);
      saveSelectedProject(projectId);
      setBlockID(null); // Reset block filter when project changes
      setPage(1); // Reset to first page
    }
  }, [projectFieldValue]);

  const totalPages = Invoices?.meta ? Math.ceil(Invoices?.meta.total / limit) : 1;

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const [blocksDynaWithLevels, setBlocksDynaWithLevels] = useState([]);

  // Track page view
  useEffect(() => {
    mixpanel.track('Invoices List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  const InvoiceFormat = [
    'id',
    'unit',
    'block',
    'name',
    'lead_id',
    'total_amount',
    'invoice_numbers',
    'paidAmount',
    'dueAmount',
    'last_paid_on',
    'Perc_pending',
    'due_price'
  ];

  const InvoiceData = useMemo(() => {
    if (!Invoices?.data) return [];

    return Invoices?.data?.map((item) =>
      transformData(
        {
          ...item,
          id: item.id,
          unit: item?.unit?.unitName || '-',
          block: item?.unit?.block?.name || '-',
          name: item?.lead?.user?.name || '-',
          lead_id: item?.lead_id,
          total_amount: item?.total_price || 0,
          invoice_numbers: item.noOfInvoice || 0,
          paidAmount: item?.paidAmount || 0,
          dueAmount: item?.dueAmount || '-',
          last_paid_on: item?.closed_at
            ? new Date(item.closed_at).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })
            : '-',
          Perc_pending: item?.duePercentage || 0,
          due_price: item?.due_price || 0
        },
        InvoiceFormat
      )
    );
  }, [Invoices?.data]);

  console.log({ InvoiceData });

  useEffect(() => {
    if (blocksDyna) {
      const blocksWithLevels = blocksDyna.filter((block) => block?.levels?.length > 0);
      const sorted = blocksWithLevels.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setBlocksDynaWithLevels(sorted);

      if (sorted.length > 0) {
        // Transform blocks data for dropdown
        const transformedBlocks = sorted
          .map((block) => ({
            label: block.name || '',
            id: block.id,
            value: block.id
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setTransformedDataBlocks(transformedBlocks);
      } else {
        // No blocks found, clear the dropdown
        setTransformedDataBlocks([]);
      }
    } else {
      // No blocks data, clear everything
      setBlocksDynaWithLevels([]);
      setTransformedDataBlocks([]);
    }
  }, [blocksDyna]);

  const handleOnClickSearch = async () => {
    await refetchInvoices();
  };

  const handleSearchChange = useCallback(
    async (incomingQuery) => {
      setSearchQuery(incomingQuery);

      // Track search
      mixpanel.track('Invoices List Search', {
        user_id: userId,
        search_query: incomingQuery,
        project_id: selectedProjectId,
        timestamp: new Date().toISOString()
      });
    },
    [userId, selectedProjectId]
  );

  const handleSelectDropdownItem = (type, value) => {
    if (type === 'block') {
      setBlockID(value);
    }
    setOpenDropdown(null);
    setBlockDropdownPosition(null);
  };

  const handleHeaderDropdownClick = (type, event) => {
    event.stopPropagation();
    const newState = openDropdown === type ? null : type;
    setOpenDropdown(newState);

    if (type === 'block' && newState && blockHeaderRef.current) {
      const rect = blockHeaderRef.current.getBoundingClientRect();
      setBlockDropdownPosition({
        left: rect.left,
        top: rect.bottom,
        width: rect.width
      });
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.dropdown-container')) {
        setOpenDropdown(null);
        setBlockDropdownPosition(null);
      }
    };

    const handleResize = () => {
      if (openDropdown === 'block' && blockHeaderRef.current) {
        const rect = blockHeaderRef.current.getBoundingClientRect();
        setBlockDropdownPosition({
          left: rect.left,
          top: rect.bottom,
          width: rect.width
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [openDropdown]);

  const { mutate: handleDownloadNoDue } = useMutation({
    mutationFn: async ({ id }) => {
      return await downloadNoDue(id);
    },
    onSuccess: (data, variables) => {
      const { id, customerName } = variables;
      if (!data || data.size === 0) {
        toast.error('Error: File data is empty or invalid.');
        return;
      }
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      // Sanitize customer name for filename
      const sanitizedName = customerName?.replace(/[^a-zA-Z0-9]/g, '_') || 'Customer';
      a.download = `No_Due_${sanitizedName}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('No Due Downloaded');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Error in downloading No Due');
    }
  });

  const { mutate: handleDownloadLedger } = useMutation({
    mutationFn: async ({ id }) => {
      return await downloadLedger(id);
    },
    onSuccess: (data, variables) => {
      const { id, customerName } = variables;
      if (!data || data.size === 0) {
        toast.error('Error: File data is empty or invalid.');
        return;
      }
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      // Sanitize customer name for filename
      const sanitizedName = customerName?.replace(/[^a-zA-Z0-9]/g, '_') || 'Customer';
      a.download = `Ledger_${sanitizedName}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Ledger Downloaded');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Error in downloading Ledger');
    }
  });

  return (
    <>
      <Loader
        isVisible={isSubmitting || isFetchingBlocks || isFetchingInvoices || isProjectsLoading}
        message={'Loading...'}
      />

      {/* Main content */}
      <Layout>
        {/* Header */}
        <div
          className={`fixed top-0 z-10 transition-all duration-300 ease-in-out bg-white
    ${collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'}
  `}>
          <Header data={{ name: `Invoices` }} />
        </div>
        {/* {isPaid && (
        <Modal isDirty={true} confirmOnClose={true} width={55} onClose={() => setIsPaid(!isPaid)}>
          <div>
            <div className="flex justify-center mb-8 text-xl font-semibold">
              <p>Do you want to mark this paid ?</p>
            </div>
            <div className="flex gap-8 justify-center">
              <Button displayType={'secondary'}>No</Button>
              <Button onClick={handleUpdateStatus}>Yes</Button>
            </div>
          </div>
        </Modal>
      )} */}

        {/* <div className="flex z-10 items-center justify-between px-6 py-4 bg-white fixed top-[9rem] border-t-[2px] border-stone-100 left-[18vw] w-[82vw] shadow-sm"> */}
        <div
          className={`flex items-center justify-between px-6 py-4 bg-white fixed top-[4rem] border-t-[2px] border-stone-100 shadow-sm z-[10] transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          {/* Pass searchQuery and handleSearchChange as props to Search component */}
          <div className="flex items-center gap-4">
            <Search
              onChange={handleSearchChange}
              query={searchQuery}
              onClick={handleOnClickSearch}
            />

            {/* Project Dropdown */}
            <FormInput
              width={40}
              label={''}
              register={register}
              register_key={'project'}
              isRequired={true}
              type={'dropdown'}
              options={[
                { label: 'Select Project', value: '', disabled: true, selected: true },
                ...projectsForDropdown
              ]}
              validation={{ required: 'Project is required' }}
              errors={errors}
              hideAstrik={true}
              mb
            />
          </div>

          <div className="flex gap-5 cursor-pointer">
            <div className="flex items-center gap-4">
              {/* Total Pending Amount */}
              <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-200">
                <div className="text-xs text-gray-500 font-medium">Total Pending Amount</div>
                <div className="text-lg font-bold text-red-600">
                  ₹
                  {selectedProjectId && Invoices?.meta?.pendingAmount
                    ? new Intl.NumberFormat('en-IN').format(Invoices?.meta?.pendingAmount)
                    : '0'}
                </div>
              </div>

              {/* Total Pending Late Fee */}
              <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-200">
                <div className="text-xs text-gray-500 font-medium">Total Pending Late Fee</div>
                <div className="text-lg font-bold text-orange-600">
                  ₹
                  {selectedProjectId && Invoices?.meta?.pendingLateFees
                    ? new Intl.NumberFormat('en-IN').format(Invoices?.meta?.pendingLateFees)
                    : '0'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <GridWrapper width={100}>
          <div className="mt-[11rem] h-[calc(100vh-12.5rem)]   overflow-auto relative rounded-t-lg flex flex-col items-center gap-4">
            <div className="overflow-x-auto  w-full relative rounded-t-lg">
              <table className="text-left w-full min-w-[1200px]">
                <thead className="bg-gray-200 !text-gray-600 w-full sticky top-0 z-20">
                  <tr className="font-normal h-[3rem] w-full whitespace-nowrap">
                    <th className="px-4 py-2 border-b font-normal">Unit</th>
                    <th
                      className="px-4 py-2 border-b font-normal relative dropdown-container"
                      ref={blockHeaderRef}>
                      <span>Block</span>
                      <button
                        className="ml-1 align-middle"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleHeaderDropdownClick('block', e);
                        }}>
                        <ChevronDown size={16} />
                      </button>
                    </th>
                    <th className="px-4 py-2 border-b font-normal">Customer Name</th>
                    <th className="px-4 py-2 border-b font-normal">Sales Amount</th>
                    <th className="px-4 py-2 border-b font-normal cursor-pointer">Total Invoice</th>
                    <th className="px-4 py-2 border-b font-normal">Amount Received</th>
                    <th className="px-4 py-2 border-b font-normal relative">
                      <div className="flex items-center justify-between">
                        <span>Due Amount</span>
                        <div className="ml-1 flex flex-col items-center justify-center">
                          <button
                            className={`w-4 h-4 ${
                              dueAmountSort === 'asc' ? 'text-blue-600' : 'text-gray-400'
                            }`}
                            onClick={() => {
                              if (dueAmountSort === 'asc') {
                                setDueAmountSort('');
                              } else {
                                setDueAmountSort('asc');
                              }
                            }}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M5 12l5-5 5 5H5z" />
                            </svg>
                          </button>
                          <button
                            className={`w-4 h-4 -mt-1 ${
                              dueAmountSort === 'desc' ? 'text-blue-600' : 'text-gray-400'
                            }`}
                            onClick={() => {
                              if (dueAmountSort === 'desc') {
                                setDueAmountSort('');
                              } else {
                                setDueAmountSort('desc');
                              }
                            }}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M15 8l-5 5-5-5h10z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </th>

                    <th className="px-4 py-2 border-b font-normal">Last Paid On</th>
                    <th className="px-4 py-2 border-b font-normal relative">
                      <div className="flex items-center justify-between">
                        <span>Pending %</span>
                        <div className="ml-1 flex flex-col items-center justify-center">
                          <button
                            className={`w-4 h-4 ${
                              sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-400'
                            }`}
                            onClick={() => {
                              if (sortDirection === 'asc') {
                                setSortDirection('');
                              } else {
                                setSortDirection('asc');
                              }
                            }}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M5 12l5-5 5 5H5z" />
                            </svg>
                          </button>
                          <button
                            className={`w-4 h-4 -mt-1 ${
                              sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-400'
                            }`}
                            onClick={() => {
                              if (sortDirection === 'desc') {
                                setSortDirection('');
                              } else {
                                setSortDirection('desc');
                              }
                            }}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M15 8l-5 5-5-5h10z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </th>
                    <th className="px-4 py-2 border-b font-normal">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {InvoiceData && InvoiceData?.length > 0 ? (
                    InvoiceData?.map((invoice, index) => {
                      return (
                        <React.Fragment key={invoice?.id || index}>
                          <tr
                            className="border-b hover:bg-gray-50 bg-white h-[3.5rem]"
                            onClick={() => navigate(`/builder/accounts/invoice/${invoice?.id}`)}>
                            <td className="px-4 py-2 text-sm">{invoice?.unit || '---'}</td>
                            <td className="px-4 py-2 text-sm">{invoice?.block || '---'}</td>
                            <td className="px-4 py-2 text-sm">{invoice?.name || '---'}</td>
                            <td className="px-4 py-2 text-sm">{invoice?.total_amount || 0}</td>
                            <td className="px-4 py-2 text-sm">{invoice?.invoice_numbers || 0}</td>
                            <td className="px-4 py-2 text-sm">
                              {`₹${invoice?.paidAmount.toLocaleString('en-IN') || 0}`}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              {`₹${invoice?.dueAmount.toLocaleString('en-IN') || 0}`}
                            </td>
                            <td className="px-4 py-2 text-sm">{invoice?.last_paid_on || 0}</td>
                            <td className="px-4 py-2 text-sm">{invoice?.Perc_pending}%</td>
                            <td className="px-4 py-2 text-sm">
                              <div className="flex items-center gap-2">
                                {Number(invoice?.due_price) === 0 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadNoDue({
                                        id: invoice?.id,
                                        customerName: invoice?.name
                                      });
                                    }}
                                    className="p-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors duration-200"
                                    title="Download No Due Certificate">
                                    <FileText size={16} />
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadLedger({
                                      id: invoice?.id,
                                      customerName: invoice?.name
                                    });
                                  }}
                                  className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors duration-200"
                                  title="Download Ledger">
                                  <BookOpen size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={12} className="text-center py-10 align-middle">
                        <div className="flex flex-col items-center justify-center w-full h-full">
                          <h2 className="text-lg font-bold text-gray-800 mb-1">No Data Found</h2>
                          <p className="text-gray-600 text-sm">
                            The page you are trying to access has no data.
                            <br />
                            Proceed with adding data.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {InvoiceData && InvoiceData?.length > 0 && (
              <PaginationComponent
                page={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isFetching={isFetchingInvoices}
              />
            )}
          </div>
        </GridWrapper>
      </Layout>

      {/* Block Dropdown Portal */}
      {openDropdown === 'block' &&
        blockDropdownPosition &&
        createPortal(
          <div
            className="bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
            style={{
              position: 'fixed',
              left: blockDropdownPosition.left,
              top: blockDropdownPosition.top + 4,
              minWidth: blockDropdownPosition.width,
              zIndex: 1000
            }}
            onMouseDown={(e) => e.stopPropagation()}>
            {[
              {
                id: '',
                value: '',
                label: blockID ? 'Clear Filter' : 'Select Block',
                selected: true
              },
              ...transformedDataBlocks
            ].map((proj, idx) => (
              <div
                key={proj.id || idx}
                className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                  proj.id === '' && 'bg-gray-100'
                } ${proj.id === blockID && 'bg-blue-100'}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelectDropdownItem('block', proj.id);
                }}>
                {proj.label}
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

export default InvoicesList;
